import React, { useState, useCallback, useMemo, useEffect } from 'react';
// @mui
import {
  Container,
  Box,
  Typography,
  Alert,
  Stack,
  Button
} from '@mui/material';
// @iconify
import { Icon } from '@iconify/react';
// components
import Page from '../../components/Page';
import {
  TariffList,
  TariffForm,
  TariffView
} from '../../components/ocpi/tariffs';
import MuiConfirmationDialog from '../../components/@material-extend/MuiConfirmationDialog';
// hooks
import { useNavigate } from 'react-router-dom';
import { useFetchOnDemand } from 'src/hooks/useFetch.hook';
import { OCPI_ROLES } from 'src/utils/ocpi.constants';

export default function OCPITariffs() {
  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('All Types');
  const [partyFilter, setPartyFilter] = useState('All PartyId');
  const [selectedTariffIds, setSelectedTariffIds] = useState([]);
  const { isLoading, fetchData, data } = useFetchOnDemand("getAllTariffs", false, false);
  const [pagination, setPagination] = useState({
    offset: 0,
    limit: 50,
    total: data?.data?.length || 0
  });

  useEffect(() => {
    const params = {
      offset: pagination.offset,
      limit: pagination.limit,
      search: searchTerm,
      type: typeFilter,
      partyId: partyFilter
    };
  

    if (searchTerm) {
      params.search = searchTerm.trim();
    }

    if (typeFilter && typeFilter !== "All Types") {
      params.type = typeFilter;
    }

    if (partyFilter && partyFilter !== "All PartyId") {
      params.partyId = partyFilter;
    }

    fetchData(params);
  }, [pagination.offset, pagination.limit, searchTerm, typeFilter, partyFilter]);
  
  // Modal states
  const [formOpen, setFormOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  
  // Form states
  const [editingTariff, setEditingTariff] = useState(null);
  const [viewingTariff, setViewingTariff] = useState(null);
  
  // Confirmation states
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState(null);

  // Navigation
  const navigate = useNavigate();

  // Mock data for development - replace with actual API calls
  const [tariffs, setTariffs] = useState(data?.data || []);
  const [error, setError] = useState(null);

  // Update pagination total when tariffs change
  React.useEffect(() => {
    setPagination(prev => ({
      ...prev,
      total: tariffs.length
    }));
  }, [tariffs]);

  // Data mapping function to convert OCPI tariff data to form format
  const mapTariffToFormData = useCallback((tariff) => {
    if (!tariff) return null;
    
    return {
      countryCode: tariff.country_code || tariff.partner_country_code || '',
      partyId: tariff.party_id || tariff.partner_party_id || '',
      id: tariff.id || tariff.tariff_id || '',
      currency: tariff.currency || 'EUR',
      type: tariff.type || tariff.tariff_type || 'REGULAR',
      elements: tariff.elements || [
        {
          price_components: [
            {
              type: 'ENERGY',
              price: 0.30,
              step_size: 1
            }
          ]
        }
      ],
      restrictions: tariff.restrictions || 'NONE',
      energy_category: tariff.energy_category || 'ELECTRICITY',
      description: tariff.description || '',
      min_price: tariff.min_price || 0,
      max_price: tariff.max_price || null,
      valid: tariff.valid !== undefined ? tariff.valid : true
    };
  }, []);

  // Filter tariffs based on search and filters
  const filteredTariffs = useMemo(() => {
    let filtered = tariffs;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(tariff => 
        (tariff.id || tariff.tariff_id)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tariff.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tariff.type?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply type filter
    if (typeFilter && typeFilter !== "All Types") {
      filtered = filtered.filter(tariff => tariff.type === typeFilter);
    }

    // Apply status filter
    if (partyFilter && partyFilter !== "All PartyId") {
      const isValid = partyFilter === "Valid";
      filtered = filtered.filter(tariff => tariff.valid === isValid);
    }

    return filtered;
  }, [tariffs, searchTerm, typeFilter, partyFilter]);

  // Handler functions
  const handleCreateTariff = () => {
    setEditingTariff(null);
    setFormOpen(true);
  };

  const handleEditTariff = (tariffId) => {
    const tariff = data.data.find(t => (t.id || t.tariff_id) === tariffId);
    const mappedTariff = mapTariffToFormData(tariff);
    setEditingTariff(mappedTariff);
    setFormOpen(true);
  };

  const handleViewTariff = (tariffId) => {
    const tariff = data.data.find(t => (t.id) === tariffId);
    setViewingTariff(tariff);
    setViewOpen(true);
  };

  const handleDeleteTariff = (tariffId) => {
    setConfirmAction('delete');
    setConfirmData({ tariffId });
    setConfirmOpen(true);
  };

  const handleFormClose = (_, reason) => {
    if (reason === 'backdropClick') return;
    setFormOpen(false);
    setEditingTariff(null);
  };

  const handleViewClose = () => {
    setViewOpen(false);
    setViewingTariff(null);
  };

  const handleViewEdit = () => {
    const mappedTariff = mapTariffToFormData(viewingTariff);
    setViewingTariff(null);
    setViewOpen(false);
    setEditingTariff(mappedTariff);
    setFormOpen(true);
  };

  const handleFormSubmit = async (tariffData, setSubmitting) => {
    try {
      setSubmitting(true);
      
      if (editingTariff) {
        // Update existing tariff
        const updatedTariffs = tariffs.map(t => 
          (t.id || t.tariff_id) === editingTariff.id 
            ? { ...t, ...tariffData, last_updated: new Date().toISOString() }
            : t
        );
        setTariffs(updatedTariffs);
      } else {
        // Create new tariff
        const newTariff = {
          ...tariffData,
          id: `TARIFF_${Date.now()}`,
          tariff_id: `TARIFF_${Date.now()}`,
          last_updated: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        setTariffs(prev => [...prev, newTariff]);
      }
      
      setFormOpen(false);
      setEditingTariff(null);
    } catch (err) {
      console.error('Failed to save tariff:', err);
      setError('Failed to save tariff. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmClose = () => {
    setConfirmOpen(false);
    setConfirmAction(null);
    setConfirmData(null);
  };

  const handleConfirm = async () => {
    if (confirmAction === 'delete' && confirmData?.tariffId) {
      try {
        const updatedTariffs = tariffs.filter(t => (t.id || t.tariff_id) !== confirmData.tariffId);
        setTariffs(updatedTariffs);
        setSelectedTariffIds(prev => prev.filter(id => id !== confirmData.tariffId));
      } catch (err) {
        console.error('Failed to delete tariff:', err);
        setError('Failed to delete tariff. Please try again.');
      }
    }
    handleConfirmClose();
  };

  const getConfirmDialogProps = () => {
    switch (confirmAction) {
      case 'delete':
        return {
          title: 'Delete Tariff',
          contentText: 'Are you sure you want to delete this tariff? This action cannot be undone.',
          okText: 'Delete',
          closeText: 'Cancel',
          severity: 'error'
        };
      default:
        return {
          title: 'Confirm Action',
          contentText: 'Are you sure you want to proceed?',
          okText: 'Confirm',
          closeText: 'Cancel'
        };
    }
  };

  const handleBackClick = () => {
    navigate('/ocpi/management');
  };

  const handleTariffSelect = useCallback((tariffId) => {
    setSelectedTariffIds(prev => 
      prev.includes(tariffId) 
        ? prev.filter(id => id !== tariffId)
        : [...prev, tariffId]
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedTariffIds.length === filteredTariffs.length) {
      setSelectedTariffIds([]);
    } else {
      setSelectedTariffIds(filteredTariffs.map(tariff => tariff.id || tariff.tariff_id));
    }
  }, [selectedTariffIds.length, filteredTariffs]);

  const handleClearSelection = useCallback(() => {
    setSelectedTariffIds([]);
  }, []);

  const handleActivateSelected = useCallback(() => {
    const updatedTariffs = tariffs.map(tariff => 
      selectedTariffIds.includes(tariff.id || tariff.tariff_id)
        ? { ...tariff, valid: true, last_updated: new Date().toISOString() }
        : tariff
    );
    setTariffs(updatedTariffs);
    setSelectedTariffIds([]);
  }, [selectedTariffIds, tariffs]);

  const handleDeactivateSelected = useCallback(() => {
    const updatedTariffs = tariffs.map(tariff => 
      selectedTariffIds.includes(tariff.id || tariff.tariff_id)
        ? { ...tariff, valid: false, last_updated: new Date().toISOString() }
        : tariff
    );
    setTariffs(updatedTariffs);
    setSelectedTariffIds([]);
  }, [selectedTariffIds, tariffs]);

  const handleDeleteSelected = useCallback(() => {
    setConfirmAction('delete');
    setConfirmData({ tariffIds: selectedTariffIds });
    setConfirmOpen(true);
  }, [selectedTariffIds]);

  // Check if any selected tariffs have CPO role
  const hasSelectedCPOTariffs = useMemo(() => {
    if (!selectedTariffIds.length || !data?.data) return false;
    
    return selectedTariffIds.some(tariffId => {
      const tariff = data.data.find(t => t.id === tariffId);
      return tariff?.role === OCPI_ROLES.CPO;
    });
  }, [selectedTariffIds, data?.data]);

  const handlePageChange = useCallback((page) => {
    setPagination(prev => ({
      ...prev,
      offset: page * prev.limit
    }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize) => {
    setPagination(prev => ({
      ...prev,
      limit: pageSize,
      offset: 0
    }));
  }, []);

  const handleExportAll = useCallback(() => {
    try {
      const dataStr = JSON.stringify(tariffs, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `tariffs_${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export tariffs:', err);
    }
  }, [tariffs]);

  const handleExportSelected = useCallback(() => {
    try {
      const selectedTariffs = tariffs.filter(tariff => selectedTariffIds.includes(tariff.id || tariff.tariff_id));
      const dataStr = JSON.stringify(selectedTariffs, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `selected_tariffs_${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export selected tariffs:', err);
    }
  }, [tariffs, selectedTariffIds]);

  const handleImport = useCallback((importedData) => {
    try {
      // Validate imported data
      if (Array.isArray(importedData)) {
        setTariffs(prev => [...prev, ...importedData]);
      } else {
        setError('Invalid import format. Please check your file.');
      }
    } catch (err) {
      console.error('Failed to import tariffs:', err);
      setError('Failed to import tariffs. Please check your file format.');
    }
  }, []);

  return (
    <Page title="OCPI Tariffs">
      <Container maxWidth="xl">
        <Stack spacing={3}>
          {/* Header */}
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Button
              variant="outlined"
              onClick={handleBackClick}
              startIcon={<Icon icon="eva:arrow-back-fill" width={20} height={20} />}
            >
              Back to Dashboard
            </Button>
          </Stack>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Tariff List */}
          <TariffList
            tariffs={data?.data || []}
            loading={isLoading}
            error={error?.message}
            selectedTariffIds={selectedTariffIds}
            searchTerm={searchTerm}
            typeFilter={typeFilter}
            partyFilter={partyFilter}
            pagination={pagination}
            hasSelectedCPOTariffs={hasSelectedCPOTariffs}
            onSearchChange={setSearchTerm}
            onTypeFilterChange={setTypeFilter}
            onPartyFilterChange={setPartyFilter}
            onTariffSelect={handleTariffSelect}
            onTariffView={handleViewTariff}
            onTariffEdit={handleEditTariff}
            onTariffDelete={handleDeleteTariff}
            onSelectAll={handleSelectAll}
            onClearSelection={handleClearSelection}
            onActivateSelected={handleActivateSelected}
            onDeactivateSelected={handleDeactivateSelected}
            onDeleteSelected={handleDeleteSelected}
            onExportAll={handleExportAll}
            onExportSelected={handleExportSelected}
            onImport={handleImport}
            onCreateTariff={handleCreateTariff}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />

          {/* Tariff Form Dialog */}
          <TariffForm
            open={formOpen}
            onClose={handleFormClose}
            onSubmit={handleFormSubmit}
            initialValues={editingTariff}
            isEdit={!!editingTariff}
          />

          {/* Tariff View Dialog */}
          <TariffView
            open={viewOpen}
            onClose={handleViewClose}
            onEdit={handleViewEdit}
            tariff={viewingTariff}
          />

          {/* Confirmation Dialog */}
          <MuiConfirmationDialog
            open={confirmOpen}
            onClose={handleConfirmClose}
            onOk={handleConfirm}
            {...getConfirmDialogProps()}
          />
        </Stack>
      </Container>
    </Page>
  );
}
